import { min, select } from 'd3';
import { html, render } from 'lit';
import "../../../styles/pages/Event-evaluation.scss";
import { EEventId, ICalificacionPendiente } from "../../data/models/Entities";
import { _SvCalificacionAsignar, _SvCalificacionObtenerAsistencias, _SvCalificacionObtenerPendientes } from "../../data/services/Calificacion";
import _L, { _HttpMsg } from "../../utils/Labels";
import { _DateFormatStandar, _GenerarRangoFechas, _IsValidDate } from "../../utils/Time";
import { BaseUIEvent } from "../bases/BaseUIEvent";
import { EmptyTag } from '../components/EmptyTag';
import { Input } from "../components/Input";
import { LOADING } from "../components/Loading";
import { EvaluationTable as EvaluationsTable } from '../components/Tables/EvaluationTable';
import { ShowToast, ToastType } from "../components/Toast";
import { _CICLO_ESCOLAR_CASO } from '../utils/DataCicloEscolar';
import { _CreateElementFromHTML } from '../utils/General';
import ic_logro from '/icons/event/use-icon-logro.svg?raw';
import ic_search from '/icons/ic_search.svg?raw';


type TRowEvalStatus = "eval" | "eval_empty" | "no_apply"
export interface ICalificacionP extends ICalificacionPendiente {
    _FechaInicioFmt?: string;
    _FechaEval?: Date //| "today";
    _Observacion?: string;
    _Calificacion?: string;
    _EvaluacionesConfig?: { Nombre: string; Descripcion: string; }[];

    _Status: TRowEvalStatus;
    _Valid: false;
    _PickerData: {
        min: Date
        max: Date
        lockDates: string[]
        asistencias: string[]
        checkboxActive: boolean
    };
}

/** { [`key`]: [`selector`, `label`] } */
const TableInfoCols: { [k in keyof Partial<ICalificacionP>]: [string, string] } = {
    "Materia": [".materia", _L("evaluacion.materia")],
    "_FechaInicioFmt": [".fecha_inicio", _L("evaluacion.fecha_inicio")],
    "Criterio": [".criterio", _L("evaluacion.criterio")],
    "_Calificacion": [".eval", _L("evaluacion.eval")],
    "_FechaEval": [".fecha_eval", _L("evaluacion.fecha_eval")],
    "_Observacion": [".observacion", _L("evaluacion.observacion")],
}

export class EventEvaluation extends BaseUIEvent {
    private pendientesList: ICalificacionP[];
    private dataFiltered: ICalificacionP[];
    private table: EvaluationsTable;
    private emptyTag: EmptyTag;
    private fechasAsistencias: string[];
    private searchInput: Input
    private caso_ciclo_escolar = _CICLO_ESCOLAR_CASO();

    constructor() {
        super('ui-event-evaluation', EEventId.EVALUATION);
        this.pendientesList = [];
        this.fechasAsistencias = [];

        this.InitUI();
        this.AddSaveBtn();
    }

    private InitUI() {
        // agregar información de ciclo escolar específico, si aplica
        if (this.caso_ciclo_escolar) {
            const wrapper = document.createElement("div");
            wrapper.className = "ciclo-escolar-info"
            render(html`
                <strong>${this.caso_ciclo_escolar.Nombre}</strong>
                <p>Inicio: ${_DateFormatStandar(this.caso_ciclo_escolar.FechaInicio)}</p>
                <p>Fin: ${_DateFormatStandar(this.caso_ciclo_escolar.FechaFin)}</p>
            `, wrapper);
            this.headerExtraActionsContainer.insertAdjacentElement("afterbegin", wrapper);
            console.warn("asdasd", wrapper);
        }

        this.table = new EvaluationsTable({
            beforeRender: (items) => {
                if (!items.length)
                    this.emptyTag._ShowTag()
                else
                    this.emptyTag._Remove()
            }
        })
        this.bodyContainer.append(() => this.table._Element);

        this.emptyTag = new EmptyTag(this.bodyContainer.node())
            ._SetText(_L("evaluacion.pendientes_empty"))
            ._SetSVGIcon(ic_logro)

        select(this.headerExtraActionsContainer).classed("hide", false)
        const input = new Input(this.headerExtraActionsContainer, {
            placeholder: _L("evaluacion.search_placeholder"),
            maxLength: 200,
            iconRight: _CreateElementFromHTML(ic_search),
        })
        this.searchInput = input
        let timeoutInput: NodeJS.Timeout = null
        input.input.node().oninput = () => {
            if (!!timeoutInput) {
                clearTimeout(timeoutInput)
                timeoutInput = null
            }
            timeoutInput = setTimeout(() => this.RefreshRows(), 1000);
        }

        for (const [selector, label] of Object.values(TableInfoCols)) {
            this.bodyContainer.select<HTMLTableCellElement>("thead > tr " + selector).text(label);
        }

        const alumno = this.childList[0]
        if (!alumno?.IdChild) return

        this.table._SkeletonMode(true);
        this.pendientesList = [];
        _SvCalificacionObtenerPendientes(this.caso_ciclo_escolar ? this.caso_ciclo_escolar.Id : undefined)
            .then((datos) => {
                const preProcecedData = (datos || []).filter(d => d.IdAlumno == alumno.IdChild)

                if (!preProcecedData.length) {
                    this.table._SkeletonMode(false);
                    this.RefreshRows();
                } else {
                    const verAsistenciasFechaInicio = this.caso_ciclo_escolar
                        ? new Date(this.caso_ciclo_escolar.FechaInicio)
                        : new Date(min(preProcecedData.map(d => d.DtInicio)));
                    const verAsistenciasFechaFin = this.caso_ciclo_escolar
                        ? new Date(this.caso_ciclo_escolar.FechaFin)
                        : new Date();
                    _SvCalificacionObtenerAsistencias(alumno.IdChild, verAsistenciasFechaInicio, verAsistenciasFechaFin)
                        .then(res => {
                            this.fechasAsistencias = res.Datos.sort((a, b) => new Date(a).getTime() - new Date(b).getTime()) || [];
                            this.table._SetFechasAsistencias(this.fechasAsistencias);

                            if (!this.fechasAsistencias.length) {
                                ShowToast(_L("evaluacion.title"), _L("evaluacion.no_asistencia_valida"), "warn")
                                this.btnSave.attr("disabled", true)
                            } else {
                                this.pendientesList = preProcecedData.map<ICalificacionP>(d => ({
                                    ...d,
                                    _Status: "eval_empty",
                                    _Valid: false,
                                    _PickerData: {
                                        min: new Date(d.DtInicio),
                                        max: new Date(),
                                        ...ObtenerResumenAsistenciasEnRango(new Date(d.DtInicio), verAsistenciasFechaFin, this.fechasAsistencias),
                                        checkboxActive: false,
                                    }
                                }))
                                this.pendientesList.forEach(d => CalificacionEstablecerValoresPorDefecto(d));
                            }
                        })
                        .catch((err) => {
                            console.warn(err);
                            ShowToast(_L("evaluacion.title"), _HttpMsg("calificacion/ObtenerAsistencias", -11), "error");
                        })
                        .finally(() => {
                            this.table._SkeletonMode(false);
                            this.RefreshRows();
                        });
                }
            })
            .catch((e) => {
                console.error(e);
                ShowToast(_L("evaluacion.title"), _HttpMsg("calificacion/ObtenerPendientes", -1, "consulta"), "error");
                this.table._SkeletonMode(false);
                this.RefreshRows();
            })
    }

    private GetDataFiltered(): ICalificacionP[] {
        const filterValue = this.searchInput.value?.trim() || ""
        if (!filterValue) {
            return this.pendientesList
        }
        return this.pendientesList.filter(d => {
            const evalValues: string = (
                d.Materia
                + (d.Criterio || _L("evaluacion.no_crit"))
                + (d.Tags || [])
            );
            return (
                evalValues &&
                evalValues
                    .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // remover accentos
                    .toLowerCase()
                    .includes(
                        filterValue
                            .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
                            .toLowerCase()
                    )
            );
        })
    }

    private RefreshRows() {
        this.dataFiltered = this.GetDataFiltered();
        this.table._SetItems(this.dataFiltered);
    }

    private ValidaEvaluaciones(): ICalificacionP[] | null {
        const evaluaciones: ICalificacionP[] = [];
        let errMsg: string = null;
        let errType: ToastType = null;

        for (let i = 0; i < this.dataFiltered.length; i++) {
            const c = this.dataFiltered[i];

            if (c._Status == "eval_empty") {
                continue;
            }
            const fechaEval = c._FechaEval

            if (!_IsValidDate(fechaEval)) {
                errMsg = _L("evaluacion.invalid_data");
                errType = "error";
                evaluaciones.length = 0;
                this.table._TableControl.scrollToItemIndex(i, { behavior: "smooth" });
                console.warn("fecha invalida", fechaEval, c);
                break;
            }

            if (
                (c._Status == "eval" && c._Calificacion != null && (c._Calificacion + "").trim() != "") // evaluación válida
                || (c._Status == "no_apply" && c._Calificacion === "") // "no aplica" válido
            ) {
                evaluaciones.push(c);
                continue;
            }
            else {
                this.table._TableControl.scrollToItemIndex(i, { behavior: "smooth" });
            }

            break;
        }

        if (!errType && !evaluaciones.length) {
            errMsg = _L("evaluacion.evals_required");
            errType = "warn";
        }

        if (errMsg) {
            ShowToast(_L("evaluacion.title"), errMsg, errType);
            return null;
        }

        return evaluaciones;
    }

    protected OnSaveClick(): void {
        if (this.childList.length === 0) {
            ShowToast(_L("comment.title"), _L("comment.alumnos_required"), "info");
            return;
        }

        const evalsRegistrar = this.ValidaEvaluaciones();
        if (evalsRegistrar == null) {
            return;
        }

        const res = _SvCalificacionAsignar(
            this.IS_EXTEMPORAL,
            this.childList[0].IdChild,
            evalsRegistrar.map(d => ({
                Id: d.Id,
                IdAsignacion: d.IdAsignacion,
                Calificacion: d._Calificacion,
                Observacion: d._Observacion,
                FechaEval: FixedDateEval(d._FechaEval, true).toISOString(),
            })));

        LOADING.Show();
        res
            .then(res => {
                const success = res.Resultado > 0;
                let msg = _HttpMsg("calificacion/Asignar", res.Resultado)
                if (res.Resultado == -14) {
                    msg += ":\n "
                    msg += evalsRegistrar
                        .filter((_, i) => res.IndexFechasInasistencia.includes(i))
                        .map(d => _DateFormatStandar(FixedDateEval(d._FechaEval)))
                        .join(", ")
                }
                ShowToast(_L("evaluacion.title"), msg, (success ? 'success' : "error"));
                if (success) history.back();
            })
            .catch(() => {
                ShowToast(_L("evaluacion.title"), _HttpMsg("calificacion/Asignar", -123), "error");
            })
            .finally(() => {
                LOADING.Dismiss();
            })
    }
}

export function CalificacionEstablecerValoresPorDefecto(d: ICalificacionP) {
    const ultimaFechaAsistencia = d._PickerData.asistencias[d._PickerData.asistencias.length - 1]
    d._Status = "eval_empty";
    d._Calificacion = undefined
    d._FechaInicioFmt = _DateFormatStandar(d.DtInicio, "dd/mm/yy")
    d._FechaEval = ultimaFechaAsistencia ? new Date(ultimaFechaAsistencia) : null // this.fechasAsistencias[this.fechasAsistencias.length - 1] ? new Date(this.fechasAsistencias[this.fechasAsistencias.length - 1]) : "today"
    d._Observacion = ""
    d._EvaluacionesConfig = d.EvalNombre.reduce((res, nombre, i) => {
        res.push({
            Nombre: nombre,
            Descripcion: d.EvalDescripcion[i],
        })
        return res;
    }, <ICalificacionP["_EvaluacionesConfig"]>[])
}

// *********************************************************************
// UTILIDADES
// *********************************************************************

interface ResumenAsistencias {
    asistencias: string[]
    lockDates: string[]
}

export function ObtenerResumenAsistenciasEnRango(datetInicio: Date, dateFin: Date, fechasAsistenciasTotales: string[]): ResumenAsistencias {
    const truncarFechaGetTime = (fecha: Date): Date => new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate());

    const dtsAsistenciasTotales = fechasAsistenciasTotales.map(d => new Date(d))

    const dtsFechasEnRango = _GenerarRangoFechas(
        truncarFechaGetTime(datetInicio),
        truncarFechaGetTime(new Date())
    )

    let asistencias: string[] = []
    let lockDates: string[] = []

    dtsFechasEnRango.forEach(d => {
        const fueraDeRango = (d.getTime() < datetInicio.getTime() || d.getTime() > dateFin.getTime())
        if (fueraDeRango) {
            lockDates.push(d.toISOString())
            return
        }
        const isAsistencia = dtsAsistenciasTotales.find(a =>
            a.getFullYear() == d.getFullYear()
            && a.getMonth() == d.getMonth()
            && a.getDate() == d.getDate()
        )

        if (isAsistencia) {
            asistencias.push(d.toISOString())
        } else {
            lockDates.push(d.toISOString())
        }
    })

    console.warn("asistencias", asistencias)
    console.warn("lockdates", lockDates)

    return {
        asistencias,
        lockDates
    }
}

export function FixedDateEval(fechaEval: ICalificacionP["_FechaEval"], evalToday?: boolean) {
    if (evalToday && fechaEval) {
        const dt = new Date()
        if (
            fechaEval.getFullYear() === dt.getFullYear() &&
            fechaEval.getMonth() === dt.getMonth() &&
            fechaEval.getDate() === dt.getDate()
        ) {
            return dt;
        }
    }
    return fechaEval
}
